import { Component, Input, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import UniqueEntityID from '../../core/domain/unique_entity_id';
import { Dietitian } from '../../data/dietitian/domain/dietitian';
import {
  MessageHelpClosed,
  MessageHelpCreated,
  MessageHelpOpened,
  MessageHelpUpdated,
} from '../../data/help-me/domain/events/message-help/message-help-events';
import { FaqHelp } from '../../data/help-me/domain/faq-help';
import {
  MessageAttachmentHelpProps,
  MessageHelp,
} from '../../data/help-me/domain/message-help';
import { UrlTargeting } from '../../data/help-me/domain/url-targeting';
import { IncomingMessageEventProvider } from '../../data/notification/incoming-message-event-provider';
import { HelpMeFacade } from './help-me.facade';
import { HelpMeCategories, HelpMeCategoriesTools } from './help-me-categories';

export const SCREENSHOT_MIME_TYPES = ['image/jpeg', 'image/png'];

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-help-me',
  templateUrl: './help-me.component.html',
  styleUrls: ['./help-me.component.scss'],
  providers: [HelpMeFacade],
})
export class HelpMeComponent implements OnDestroy {
  @Input() public dietitian: Dietitian | undefined;
  public isClosed = true;
  public faqHelp: FaqHelp[] | undefined;
  public messageHelp: MessageHelp[] | undefined;
  public hasMessager = false;
  public category: HelpMeCategories | undefined = undefined;
  public content: string | undefined = undefined;
  public acceptedFiles = SCREENSHOT_MIME_TYPES.join(',');
  public attachments: MessageAttachmentHelpProps[] = [];
  public submitted = false;
  public badged = false;
  public readonly HelpMeCategoriesTools = HelpMeCategoriesTools;
  private messageHelpEventSubscription: Subscription;

  public get isLoading() {
    return this.isFaqHelpLoading || this.isMessageLoading;
  }

  private isFaqHelpLoading = false;
  private isMessageLoading = false;
  private currentUrl: string;
  private routerSubscription: Subscription;
  private urlTargeting: UrlTargeting[] | undefined;
  private notificationSubscription: Subscription;

  constructor(
    router: Router,
    incomingMessageEventProvider: IncomingMessageEventProvider,
    private facade: HelpMeFacade,
    private toastrService: ToastrService,
  ) {
    this.routerSubscription = router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url =
          event.url.indexOf('?') >= 0 ? event.url.split('?')[0] : event.url;
        if (this.currentUrl !== url) {
          this.currentUrl = this.completeUrl(url);
          if (!this.isClosed) {
            this.loadFaqHelp();
          } else {
            this.loadMessage();
          }
        }
      }
    });

    this.notificationSubscription =
      incomingMessageEventProvider.events$.subscribe((event) => {
        if (
          event &&
          event.entity?.props &&
          // eslint-disable-next-line no-prototype-builtins
          event.entity.props.hasOwnProperty('messageHelpId')
        ) {
          this.loadMessage();
        }
      });

    facade
      .loadUrlTargeting()
      .then((urlTargeting) => (this.urlTargeting = urlTargeting));
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    this.messageHelpEventSubscription?.unsubscribe();
    this.notificationSubscription?.unsubscribe();
  }

  unauthenticatedUrl(): boolean {
    return (
      this.currentUrl.startsWith('/auth/sign-in') ||
      this.currentUrl.startsWith('/auth/sign-up') ||
      this.currentUrl.startsWith('/auth/password-reset')
    );
  }

  async onTabClick() {
    this.isClosed = !this.isClosed;
    if (!this.isClosed) {
      this.loadMessage();
      this.loadFaqHelp();
      this.messageHelpEventSubscription =
        this.facade.messageHelpEventProvider$.subscribe((event) => {
          if (event instanceof MessageHelpCreated) {
            this.toastrService.success(
              "La question/demande d'aide a été enregistrée",
            );
          } else if (event instanceof MessageHelpUpdated) {
            this.toastrService.success('La réponse a été enregistrée');
          } else if (event instanceof MessageHelpClosed) {
            this.toastrService.success(
              "La question/demande d'aide a été fermée",
            );
          } else if (event instanceof MessageHelpOpened) {
            this.toastrService.success(
              "La question/demande d'aide a été ouverte",
            );
          }
        });
    } else {
      this.messageHelpEventSubscription?.unsubscribe();
    }
  }

  public loadMessage() {
    if (this.dietitian) {
      this.isMessageLoading = true;
      this.facade
        .loadMessage(this.dietitian)
        .then((results) => {
          this.messageHelp = results;
          this.isMessageLoading = false;
          this.badged = this.isBadged();
        })
        .catch(() => {
          this.isMessageLoading = false;
        });
    } else {
      this.isMessageLoading = false;
    }
  }

  private loadFaqHelp() {
    const target = this.getUrlTargetingWithUrl();
    if (target) {
      this.isFaqHelpLoading = true;
      this.facade
        .loadFaqHelp(target)
        .then((faq) => {
          this.faqHelp = faq;
          this.isFaqHelpLoading = false;
        })
        .catch(() => (this.isFaqHelpLoading = false));
    } else {
      this.faqHelp = [];
    }
  }

  private getUrlTargetingWithUrl() {
    if (this.urlTargeting) {
      for (const urlTargeting of this.urlTargeting) {
        const regexp = new RegExp(urlTargeting.url);
        if (regexp.test(this.currentUrl)) {
          return urlTargeting.target;
        }
      }
    }
    return undefined;
  }

  openMessager() {
    this.hasMessager = true;
  }

  addAttachment(event: Event) {
    if (
      !(event.target instanceof HTMLInputElement) ||
      !event.target.files ||
      event.target.files.length == 0
    ) {
      return;
    }

    const file: File = event.target.files[0];
    this.attachments.push({
      file,
      status: 'init',
      name: file.name,
      size: file.size,
      mimeType: file.type,
    } as MessageAttachmentHelpProps);
    event.target.value = '';
  }

  removeAttachment(attachment: MessageAttachmentHelpProps) {
    for (let i = 0; i < this.attachments.length; i++) {
      this.attachments.splice(this.attachments.indexOf(attachment), 1);
    }
  }

  closeMessager() {
    this.hasMessager = false;
    this.category = undefined;
    this.content = undefined;
    this.attachments = [];
  }

  enablePostMessager() {
    return this.category && this.hasContentValid();
  }

  hasContentValid() {
    return (
      this.content && this.content.length > 0 && this.content.length < 4000
    );
  }

  async postMessager() {
    if (!this.submitted) {
      this.submitted = true;
      const messageHelp: MessageHelp = MessageHelp.create(
        {
          dietitianId: this.dietitian?.id.toString(),
          dietitianFullName: this.dietitian?.fullName,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          category: this.category!,
          answered: false,
          closed: false,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          content: this.content!,
          createdAt: new Date(),
          attachments: this.attachments,
        },
        new UniqueEntityID(uuidv4()),
      );
      await this.facade.postMessage(messageHelp);
      this.closeMessager();
      this.submitted = false;
      this.loadMessage();
    }
  }

  getLabelCategory(category: HelpMeCategories) {
    return HelpMeCategoriesTools.getLabelCategory(category, false);
  }

  displayCategory(category: HelpMeCategories) {
    if (this.unauthenticatedUrl()) {
      return HelpMeCategoriesTools.displayUnauthenticatedCategory(category);
    }
    return true;
  }

  private completeUrl(url: string) {
    switch (url) {
      case '/dashboard/templates':
        return url + '/list';
      case '/dashboard/questions':
        return url + '/patients';
      case '/dashboard/trackers':
        return url + '/profil';
      case '/dashboard/billing':
        return url + '/current';
      case '/dashboard/account':
        return url + '/informations';
      default:
        return url;
    }
  }

  private isBadged() {
    let result = false;
    if (this.messageHelp) {
      for (const message of this.messageHelp) {
        if (
          message.thread &&
          message.thread.length > 0 &&
          message.thread[message.thread.length - 1].supportReply
        ) {
          result = true;
          break;
        }
      }
    }
    return result;
  }

  authorizedUrl(): boolean {
    return (
      !this.currentUrl?.startsWith('/survey') &&
      !this.currentUrl?.startsWith('/my') &&
      !this.currentUrl?.startsWith('/auth/my')
    );
  }
}
